import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqList } from '@/mocks/faq';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredArticles = articles.slice(0, 6);

  const toggleFaq = (id: number) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '俄罗斯妈妈 - 走进真实的俄罗斯妈妈世界',
          description: '探索俄罗斯妈妈的真实生活、育儿理念与家庭文化。了解俄罗斯妈妈如何在传统与现代之间平衡家庭、教育与自我成长。',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[500px] md:h-[650px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20Russian%20home%20living%20room%20with%20a%20mother%20and%20child%20sitting%20on%20a%20wooden%20floor%20surrounded%20by%20traditional%20Russian%20textiles%20and%20decor%2C%20soft%20afternoon%20sunlight%20streaming%20through%20lace%20curtains%2C%20books%20and%20toys%20scattered%20around%2C%20samovar%20on%20a%20table%2C%20warm%20cream%20and%20terracotta%20tones%2C%20emotional%20editorial%20photography%2C%20intimate%20family%20moment&width=1800&height=1000&seq=hero-main-2026&orientation=landscape"
              alt="俄罗斯妈妈 - 走进真实的俄罗斯妈妈世界"
              title="俄罗斯妈妈 - 育儿理念与家庭文化"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

            <div className="relative z-10 w-full px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-5 leading-tight">
                走进真实的
                <br className="md:hidden" />
                <span className="text-accent-400">俄罗斯妈妈</span>世界
              </h1>
              <p className="text-base md:text-lg text-background-50/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                探索俄罗斯妈妈的真实生活、育儿理念与家庭文化。了解她们如何在传统与现代之间平衡家庭、教育与自我成长，感受来自俄罗斯妈妈群体的温暖与力量。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/parenting-style"
                  className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  探索育儿方式
                </Link>
                <Link
                  to="/about"
                  className="px-6 py-3 rounded-full border border-background-50/30 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解我们
                </Link>
              </div>
            </div>
          </section>

          {/* Categories Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto w-full">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  探索分类
                </h2>
                <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                  从育儿方式到跨文化故事，六大分类带你全面了解俄罗斯妈妈的世界
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="group relative h-56 rounded-lg overflow-hidden cursor-pointer"
                  >
                    <img
                      src={cat.bgImage}
                      alt={cat.name}
                      title={cat.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-8 h-8 flex items-center justify-center">
                          <i className={`${cat.icon} text-accent-400 text-lg`}></i>
                        </span>
                        <h3 className="text-lg font-heading font-semibold text-background-50">{cat.name}</h3>
                      </div>
                      <p className="text-sm text-background-50/70 line-clamp-2">{cat.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto w-full">
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    精选文章
                  </h2>
                  <p className="text-sm text-foreground-500">深入了解俄罗斯妈妈的育儿智慧与生活哲学</p>
                </div>
                <Link
                  to="/category/parenting-style"
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>
            </div>
          </section>

          {/* About Snippet Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-5xl mx-auto w-full text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                关于「俄罗斯妈妈」
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto mb-8 leading-relaxed">
                我们是一群对俄罗斯家庭文化充满热情的中俄文化交流者。通过真实的故事、深度的内容和温暖的社区，我们希望搭建一座理解与共鸣的桥梁，让中文世界的读者感受俄罗斯妈妈的育儿智慧与生活美学。
              </p>
              <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
                <div className="text-center">
                  <p className="text-3xl font-heading font-bold text-primary-500">200+</p>
                  <p className="text-xs text-foreground-400 mt-1">原创文章</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-heading font-bold text-accent-500">50,000+</p>
                  <p className="text-xs text-foreground-400 mt-1">月度读者</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-heading font-bold text-secondary-500">12</p>
                  <p className="text-xs text-foreground-400 mt-1">核心作者</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-heading font-bold text-primary-500">6</p>
                  <p className="text-xs text-foreground-400 mt-1">内容专栏</p>
                </div>
              </div>
              <Link
                to="/about"
                className="inline-block mt-8 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-3xl mx-auto w-full">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">关于俄罗斯妈妈的育儿与家庭文化，这里有你关心的答案</p>
              </div>

              <SchemaOrg
                type="FAQPage"
                data={{ questions: faqList.map((f) => ({ question: f.question, answer: f.answer })) }}
              />

              <div className="space-y-3">
                {faqList.map((faq) => (
                  <div
                    key={faq.id}
                    className="bg-background-50 rounded-lg border border-background-200/50 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFaq(faq.id)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                    >
                      <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                        {faq.question}
                      </span>
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 flex-shrink-0">
                        <i
                          className={`ri-add-line text-foreground-500 transition-transform duration-300 ${
                            openFaq === faq.id ? 'rotate-45' : ''
                          }`}
                        ></i>
                      </span>
                    </button>
                    <div
                      className={`px-5 overflow-hidden transition-all duration-300 ${
                        openFaq === faq.id ? 'pb-5 max-h-96' : 'max-h-0'
                      }`}
                    >
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}